import { UserIcon } from '@heroicons/react/outline';
import clsx from 'clsx';
import React, { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { APP_HOME_URL, DEFAULT_THEME, LOGIN_URL } from '@/config';
import { classToggle, getPublicUrlByPath, getRegisterUrl, themes } from '@/utils/helpers';

import { Button } from '../Elements';

const PublicHeaderWrap = styled.div`
  .md__header__submenu {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.2s ease-out;
  }
  .md__header__nav__item.opened .md__header__submenu {
    margin-bottom: 12px;
    max-height: 1000px;
    transition: max-height 0.6s ease-in;
  }
  #nav-toggle2.open > div > span:nth-child(1) {
    transform: rotate(45deg);
  }
  #nav-toggle2.open > div > span:nth-child(2) {
    opacity: 0;
  }
  #nav-toggle2.open > div > span:nth-child(3) {
    transform: rotate(-45deg);
  }
  .sub__menu {
    opacity: 0;
    pointer-events: none;
    transition: none;
    min-width: 300px;
    position: absolute;
    left: 0;
    padding: 20px 10px;
    text-align: left;
    margin-top: 15px;
    background: #fed925;
    border-radius: 0 12px 12px;
    box-shadow: 0 22px 24px -20px rgb(0 0 0 / 20%);
  }
  .menu__item--with_children:hover .sub__menu {
    opacity: 1;
    pointer-events: all;
  }
  .sub__menu a {
    display: block;
    font-size: 18px;
    line-height: 1.33;
    position: relative;
    padding: 12px 20px 12px 45px;
    text-transform: none;
    transition: 0.5s;
    color: #000;
  }
  .sub__menu a span {
    position: absolute;
    left: 25px;
    font-weight: bolder;
  }
  .sub__menu a:hover {
    color: #343434;
    text-decoration: none;
  }
  @media (min-width: 1199.99px) {
    .sub__menu {
      display: block !important;
    }
  }
  @media only screen and (max-width: 835px) {
    .md__header__nav__item:after {
      color: #ffffff;
      content: '>';
      position: absolute;
      right: 15px;
      top: 12px;
      transition: transform 0.2s ease-in;
    }
    .md__header__nav__item.opened:after {
      transform: rotate(90deg);
    }
  }
  @media only screen and (max-width: 1520px) {
    .md__header__nav__item a {
      font-size: 13px;
      margin-left: 0;
      margin-right: 0;
    }
  }
`;

export const PublicHeader = () => {
  const { t } = useTranslation();

  const headerNavigationBarContainerStyle = () =>
    'md__header__nav absolute lg:relative top-[50px] lg:top-0 left-0 right-0 bottom-0 flex-grow lg:flex lg:items-center lg:w-auto pt-6 lg:pt-0 max-w-none lg:max-w-4xl bg-md-yellow lg:bg-md-white mt-4 lg:mt-0 z-[55] hidden';

  const headerNavigationListItem = () =>
    'md__header__nav__item pt-4 pr-11 pb-4 pl-6 relative border-b-[1px] border-md-paleBrown lg:border-b-0';

  const headerLinkStyle = () =>
    'block font-black lg:font-bold text-black uppercase hover:text-black hover:no-underline active:text-black active:no-underline focus:text-black focus:no-underline text-lg whitespace-nowrap';

  const headerLinkHasChildren = () => 'hover:bg-md-yellow rounded-t-xl';

  const headerSubLinkStyle = () =>
    'text-black hover:text-black hover:no-underline text-sm block pl-8 py-4';

  const hamburgerBarStyle = () =>
    'block absolute h-0.2 w-5 bg-current transform transition duration-500 ease-in-out';

  const getLogoUrl = () =>
    themes[DEFAULT_THEME].assetsURL + 'globals/src/images/logos/logo_MDirector.png';

  const toggleHeaderMenu = () => {
    const headerMenu = document.getElementById('nav-content');
    const headerMenuButton = document.getElementById('nav-toggle2');

    if (headerMenu && headerMenuButton) {
      classToggle(headerMenu, 'hidden', 'open');
      classToggle(headerMenuButton, 'open');
      classToggle(document.body, 'overflow-hidden');
    }
  };

  const toggleHeaderSubMenu = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    (e.target as Element)?.parentElement?.classList.toggle('opened');
  };

  return (
    <header className="w-full text-black bg-white bg-white z-10">
      <PublicHeaderWrap>
        <div className="flex flex-wrap justify-between px-18px py-2.5  lg:py-0 lg:px-9 mx-auto lg:flex-row items-center lg:items-baseline">
          <div className="block lg:hidden mr-4">
            <Button
              id="nav-toggle2"
              size="free"
              className="text-gray-500 w-10 h-10 relative focus:outline-none bg-white"
              onClick={toggleHeaderMenu}
              variant="raw"
              title={t('productTypes.emailMarketing.title')}
            >
              <span className="sr-only">Open main menu</span>
              <div className="block w-5 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span aria-hidden="true" className={`${hamburgerBarStyle()} translate-y-1.5`} />
                <span aria-hidden="true" className={hamburgerBarStyle()} />
                <span aria-hidden="true" className={`${hamburgerBarStyle()} -translate-y-1.5`} />
              </div>
            </Button>
          </div>

          <div className="flex items-center flex-shrink-0 ml-1 lg:ml-0">
            <a
              href={APP_HOME_URL}
              title="MDirector"
              className="cursor-pointer relative top-0 lg:top-[30px]"
            >
              <img className="h-[53px] w-auto" src={`${getLogoUrl()}`} alt="MDirector" />
            </a>
          </div>
          <div className={`${headerNavigationBarContainerStyle()} lg:hidden`} id="nav-content">
            <ol className="list-reset lg:flex flex-1 items-center 222" role="menu">
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--solutions"
                  href={t('PublicHeader.Solutions.url')}
                  className={`${headerLinkStyle()} lg:ml-6`}
                  onClick={toggleHeaderSubMenu}
                >
                  {t('PublicHeader.Solutions.title')}
                </a>
                <ol
                  className="md__header__submenu block lg:hidden"
                  role="menu"
                  aria-labelledby="md__header__submenu__trigger--solutions"
                >
                  <li className="leading-3">
                    <a
                      href={t('PublicFooter.Solutions.emailMarketingURL')}
                      title={t('PublicFooter.Solutions.emailMarketing')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicFooter.Solutions.emailMarketing')}
                    </a>
                  </li>
                  <li className="leading-3">
                    <a
                      href={t('PublicFooter.Solutions.smsMarketingURL')}
                      title={t('PublicFooter.Solutions.smsMarketing')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicFooter.Solutions.smsMarketing')}
                    </a>
                  </li>
                  <li className="leading-3">
                    <a
                      href={t('PublicFooter.Solutions.landingOptimizerURL')}
                      title={t('PublicFooter.Solutions.landingOptimizer')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicFooter.Solutions.landingOptimizer')}
                    </a>
                  </li>
                  <li className="leading-3">
                    <a
                      href={t('PublicFooter.Solutions.marketingAutomationURL')}
                      title={t('PublicFooter.Solutions.marketingAutomation')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicFooter.Solutions.marketingAutomation')}
                    </a>
                  </li>
                </ol>
              </li>
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--rates"
                  href={t('PublicHeader.Rates.url')}
                  className={headerLinkStyle()}
                  onClick={toggleHeaderSubMenu}
                >
                  {t('PublicHeader.Rates.title')}
                </a>
                <ol
                  className="md__header__submenu block lg:hidden"
                  role="menu"
                  aria-labelledby="md__header__submenu__trigger--about"
                >
                  <li className="leading-3">
                    <a
                      href={getPublicUrlByPath(t('PublicHeader.Comparator.url'))}
                      title={t('PublicHeader.Comparator.title')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicHeader.Comparator.title')}
                    </a>
                  </li>
                  <li className="leading-3">
                    <a
                      href={getPublicUrlByPath(t('PublicHeader.FAQs.url'))}
                      title={t('PublicHeader.FAQs.title')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicHeader.FAQs.title')}
                    </a>
                  </li>
                </ol>
              </li>
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--support"
                  href={t('PublicHeader.Support.url')}
                  className={headerLinkStyle()}
                  onClick={toggleHeaderSubMenu}
                >
                  {t('PublicHeader.Support.title')}
                </a>
              </li>
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--about"
                  href={t('PublicHeader.About.url')}
                  className={headerLinkStyle()}
                  onClick={toggleHeaderSubMenu}
                >
                  {t('PublicHeader.About.title')}
                </a>
                <ol
                  className="md__header__submenu block lg:hidden"
                  role="menu"
                  aria-labelledby="md__header__submenu__trigger--about"
                >
                  <li className="leading-3">
                    <a
                      href={t('PublicFooter.About.companyURL')}
                      title={t('PublicFooter.About.company')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicFooter.About.company')}
                    </a>
                  </li>
                  <li className="leading-3">
                    <a
                      href={t('PublicFooter.About.talentURL')}
                      title={t('PublicFooter.About.talent')}
                      className={headerSubLinkStyle()}
                    >
                      {t('PublicFooter.About.talent')}
                    </a>
                  </li>
                </ol>
              </li>
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--resources"
                  href={t('PublicHeader.Resources.url')}
                  className={headerLinkStyle()}
                  onClick={toggleHeaderSubMenu}
                >
                  {t('PublicHeader.Resources.title')}
                </a>
              </li>
              <li className={headerNavigationListItem()}>
                <a href={t('PublicHeader.Blog.url')} className={headerLinkStyle()}>
                  {t('PublicHeader.Blog.title')}
                </a>
              </li>
            </ol>
          </div>
          <div className={`${headerNavigationBarContainerStyle()} lg:block`}>
            <ol className="list-reset lg:flex flex-1 items-center 111" role="menu">
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--solutions"
                  href={t('PublicHeader.Solutions.url')}
                  className={`${headerLinkStyle()} lg:ml-4`}
                >
                  {t('PublicHeader.Solutions.title')}
                </a>
              </li>

              <li
                className={clsx(
                  headerNavigationListItem(),
                  headerLinkHasChildren(),
                  'menu__item--with_children'
                )}
              >
                <a
                  id="md__header__submenu__trigger--rates"
                  href={t('PublicHeader.Rates.url')}
                  className={headerLinkStyle()}
                >
                  {t('PublicHeader.Rates.title')}
                </a>
                <ul className="sub__menu">
                  <li>
                    <a href={getPublicUrlByPath(t('PublicHeader.Comparator.url'))}>
                      <span className="text-white pr-2">ᐳ</span>
                      {t('PublicHeader.Comparator.title')}
                    </a>
                  </li>
                  <li>
                    <a href={getPublicUrlByPath(t('PublicHeader.FAQs.url'))}>
                      <span className="text-white pr-2">ᐳ</span>
                      {t('PublicHeader.FAQs.title')}
                    </a>
                  </li>
                </ul>
              </li>

              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--support"
                  href={t('PublicHeader.Support.url')}
                  className={headerLinkStyle()}
                >
                  {t('PublicHeader.Support.title')}
                </a>
              </li>
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--about"
                  href={t('PublicHeader.About.url')}
                  className={headerLinkStyle()}
                >
                  {t('PublicHeader.About.title')}
                </a>
              </li>
              <li className={headerNavigationListItem()}>
                <a
                  id="md__header__submenu__trigger--resources"
                  href={t('PublicHeader.Resources.url')}
                  className={headerLinkStyle()}
                >
                  {t('PublicHeader.Resources.title')}
                </a>
              </li>
              <li className={headerNavigationListItem()}>
                <a href={t('PublicHeader.Blog.url')} className={headerLinkStyle()}>
                  {t('PublicHeader.Blog.title')}
                </a>
              </li>
            </ol>
          </div>
          <div className="flex ml-auto">
            <a
              className="mr-1 lg:mr-3 ml-0 py-1 lg:py-2 px-2 lg:px-6 text-lg uppercase hover:no-underline"
              href={LOGIN_URL}
              title={t('PublicHeader.Access.title')}
            >
              <span className="hidden lg:block text-lg text-black">
                {t('PublicHeader.Access.title')}
              </span>
              <UserIcon className="w-5 m-0 lg:hidden text-md-purple" />
            </a>
            <a
              className="bg-md-purple hover:bg-md-purpleAlt text-white hover:text-white active:text-white focus:text-white hover:no-underline active:no-underline focus:no-underline font-bold uppercase shadow-sm rounded-full py-0.5 lg:py-2 px-2 lg:px-6 text-lg"
              href={getRegisterUrl(t('ProductsPublicPage.registerPath'))}
              title={t('PublicHeader.Trial.title')}
            >
              <span className="hidden lg:block text-sm lg:text-lg">
                {t('PublicHeader.Trial.title')}
              </span>
              <span className="lg:hidden text-xs relative -top-0.5">
                {t('PublicHeader.Trial.titleShort')}
              </span>
            </a>
          </div>
        </div>
      </PublicHeaderWrap>
    </header>
  );
};
